import { supabase } from '@/lib/supabase';
import { fetchAllRows } from '@/lib/fetchAllRows';
import type { Expense, ExpenseSplit, CreateExpenseData, SplitType } from '@sharecost/shared';
import { toMinorUnits, getMinorUnitsSync, splitEqually } from '@sharecost/shared';
import { resolvePeriod, DEFAULT_PERIOD_SETTINGS } from '@sharecost/shared';
import type { PeriodSettings } from '@sharecost/shared';

export interface ExpenseWithDetails extends Expense {
  splits: (ExpenseSplit & { user?: { id: string; display_name: string; avatar_url?: string } })[];
  paid_by_user?: { id: string; display_name: string; avatar_url?: string };
  category?: { id: string; name: string; icon?: string; color?: string };
}

// --- Cross-group expenses types ---

/** Subset of the shared period presets offered in the expense list filter. */
export const EXPENSE_DATE_PRESETS = ['thisMonth', 'lastMonth', 'last3Months', 'thisYear', 'all'] as const;
export type ExpenseDatePreset = (typeof EXPENSE_DATE_PRESETS)[number];

export function isExpenseDatePreset(value: string | null): value is ExpenseDatePreset {
  return value !== null && (EXPENSE_DATE_PRESETS as readonly string[]).includes(value);
}

export interface AllExpensesFilters {
  search?: string;
  groupIds?: string[];
  categoryIds?: string[];
  paidBy?: string;
  datePreset?: ExpenseDatePreset;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  myExpensesOnly?: boolean;
  sortBy?: 'date' | 'amount' | 'description';
  sortOrder?: 'asc' | 'desc';
}

export interface ExpenseWithGroup extends ExpenseWithDetails {
  group?: { id: string; name: string; color: string | null; currency_code: string };
}

export interface AllExpensesResult {
  expenses: ExpenseWithGroup[];
  totalCount: number;
}

export const ALL_EXPENSES_PAGE_SIZE = 30;

/**
 * Calendar presets follow the user's week/month start settings (shared
 * resolver, same semantics as mobile). Bounds are ISO instants so they
 * compare correctly against the timestamptz `expense_date` column.
 */
function resolveDateRange(
  preset: AllExpensesFilters['datePreset'],
  periodSettings: PeriodSettings,
): { start: string; end: string } | null {
  if (!preset) return null;
  const period = resolvePeriod(preset, periodSettings);
  if (!period) return null;
  return { start: period.start.toISOString(), end: period.end.toISOString() };
}

export async function fetchAllExpenses(
  filters: AllExpensesFilters,
  offset: number,
  limit: number = ALL_EXPENSES_PAGE_SIZE,
  // Non-archived group IDs the user can see. Without this, the query falls back
  // to RLS, which returns every group the user belongs to — including ones they
  // archived. Pass the caller's non-archived group list to scope it correctly.
  scopeGroupIds?: string[],
  // Week/month start preferences of the signed-in user (drives the date presets)
  periodSettings: PeriodSettings = DEFAULT_PERIOD_SETTINGS,
): Promise<AllExpensesResult> {
  let query = supabase
    .from('expenses')
    .select(`
      *,
      expense_splits(
        id, expense_id, user_id, amount_minor, amount_minor_usd, split_value, _deleted,
        users(id, display_name, avatar_url)
      ),
      paid_by_user:users!paid_by(id, display_name, avatar_url),
      category:categories(id, name, icon, color),
      group:groups!group_id(id, name, color, currency_code)
    `, { count: 'exact' })
    .eq('_deleted', false);

  // Text search
  if (filters.search) {
    query = query.ilike('description', `%${filters.search}%`);
  }

  // Group filter — an explicit user-selected filter wins; otherwise restrict to
  // the caller's non-archived groups so archived-group expenses don't leak.
  const effectiveGroupIds =
    filters.groupIds && filters.groupIds.length > 0 ? filters.groupIds : scopeGroupIds;
  if (effectiveGroupIds) {
    query = query.in('group_id', effectiveGroupIds);
  }

  // Category filter
  if (filters.categoryIds && filters.categoryIds.length > 0) {
    query = query.in('category_id', filters.categoryIds);
  }

  // Paid by filter
  if (filters.paidBy) {
    query = query.eq('paid_by', filters.paidBy);
  }

  // Date range (from preset or explicit)
  const dateRange = filters.startDate && filters.endDate
    ? { start: filters.startDate, end: filters.endDate }
    : resolveDateRange(filters.datePreset, periodSettings);
  if (dateRange) {
    query = query.gte('expense_date', dateRange.start).lte('expense_date', dateRange.end);
  }

  // Amount range (in USD minor units)
  if (filters.minAmount !== undefined) {
    query = query.gte('amount_minor_usd', filters.minAmount);
  }
  if (filters.maxAmount !== undefined) {
    query = query.lte('amount_minor_usd', filters.maxAmount);
  }

  // Sorting
  const sortCol = filters.sortBy === 'amount' ? 'amount_minor_usd'
    : filters.sortBy === 'description' ? 'description'
    : 'expense_date';
  const ascending = filters.sortOrder === 'asc';
  query = query.order(sortCol, { ascending });
  if (sortCol !== 'expense_date') {
    query = query.order('expense_date', { ascending: false });
  }

  // Pagination
  query = query.range(offset, offset + limit - 1);

  const { data, error, count } = await query;
  if (error) throw error;

  const expenses: ExpenseWithGroup[] = (data ?? []).map((e: any) => ({
    ...e,
    splits: (e.expense_splits ?? [])
      .filter((s: any) => s._deleted !== true)
      .map((s: any) => ({ ...s, user: s.users })),
    paid_by_user: e.paid_by_user,
    category: e.category,
    group: e.group,
  }));

  return { expenses, totalCount: count ?? 0 };
}

export async function batchDeleteExpenses(ids: string[]): Promise<{ deleted: number; failed: number }> {
  const { data, error } = await supabase
    .from('expenses')
    .update({ _deleted: true, updated_at: new Date().toISOString() })
    .in('id', ids)
    .select('id');

  if (error) throw error;
  const deleted = data?.length ?? 0;
  return { deleted, failed: ids.length - deleted };
}

export async function batchUpdateCategory(ids: string[], categoryId: string | null): Promise<void> {
  const { error } = await supabase
    .from('expenses')
    .update({ category_id: categoryId, updated_at: new Date().toISOString() })
    .in('id', ids);

  if (error) throw error;
}

export async function fetchGroupExpenses(groupId: string): Promise<ExpenseWithDetails[]> {
  const data = await fetchAllRows(
    supabase
      .from('expenses')
      .select(`
        *,
        expense_splits(
          id, expense_id, user_id, amount_minor, amount_minor_usd, split_value, _deleted,
          users(id, display_name, avatar_url)
        ),
        paid_by_user:users!paid_by(id, display_name, avatar_url),
        category:categories(id, name, icon, color)
      `)
      .eq('group_id', groupId)
      .eq('_deleted', false)
      .order('expense_date', { ascending: false })
      .order('created_at', { ascending: false }),
  );

  return data.map(e => ({
    ...e,
    splits: (e.expense_splits ?? [])
      .filter((s: any) => s._deleted !== true)
      .map((s: any) => ({ ...s, user: s.users })),
    paid_by_user: e.paid_by_user,
    category: e.category,
  })) as ExpenseWithDetails[];
}

export async function fetchExpenseDetail(expenseId: string): Promise<ExpenseWithDetails | null> {
  const { data, error } = await supabase
    .from('expenses')
    .select(`
      *,
      expense_splits(
        id, expense_id, user_id, amount_minor, amount_minor_usd, split_value, _deleted,
        users(id, display_name, avatar_url)
      ),
      paid_by_user:users!paid_by(id, display_name, avatar_url),
      category:categories(id, name, icon, color)
    `)
    .eq('id', expenseId)
    .eq('_deleted', false)
    // maybeSingle, not single: when the expense is deleted on another device
    // the realtime-triggered refetch must resolve to null (page navigates
    // away). single() would throw PGRST116 and React Query keeps the stale
    // data on error, leaving the deleted expense on screen.
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    ...data,
    splits: (data.expense_splits ?? [])
      .filter((s: any) => s._deleted !== true)
      .map((s: any) => ({ ...s, user: s.users })),
    paid_by_user: data.paid_by_user,
    category: data.category,
  } as ExpenseWithDetails;
}

export async function fetchExchangeRate(fromCurrency: string): Promise<number> {
  if (fromCurrency === 'USD') return 1;

  const { data, error } = await supabase.rpc('get_latest_exchange_rates');
  if (error) throw error;

  const rateEntry = (data ?? []).find((r: any) => r.from_currency === fromCurrency);
  if (!rateEntry) throw new Error(`Exchange rate not found for ${fromCurrency}`);

  return rateEntry.rate;
}

function calculateSplits(
  totalMinor: number,
  totalMinorUsd: number,
  splitType: SplitType,
  splitWith: string[],
  exactAmounts?: Record<string, number>,
  percentages?: Record<string, number>,
  shares?: Record<string, number>,
  currencyCode?: string
): { userId: string; amountMinor: number; amountMinorUsd: number; splitValue?: number }[] {
  const minorUnits = getMinorUnitsSync(currencyCode ?? 'USD');

  if (splitType === 'equal') {
    const amounts = splitEqually(totalMinor, splitWith.length);
    const usdAmounts = splitEqually(totalMinorUsd, splitWith.length);
    return splitWith.map((userId, i) => ({
      userId,
      amountMinor: amounts[i],
      amountMinorUsd: usdAmounts[i],
    }));
  }

  if (splitType === 'exact' && exactAmounts) {
    let usedMinor = 0;
    let usedUsd = 0;
    const results = splitWith.map((userId, i) => {
      const userAmount = exactAmounts[userId] ?? 0;
      const userMinor = toMinorUnits(userAmount, minorUnits);
      // Proportional USD calculation
      const proportion = totalMinor > 0 ? userMinor / totalMinor : 1 / splitWith.length;
      const userUsd = i === splitWith.length - 1
        ? totalMinorUsd - usedUsd
        : Math.round(totalMinorUsd * proportion);
      usedMinor += userMinor;
      usedUsd += userUsd;
      return { userId, amountMinor: userMinor, amountMinorUsd: userUsd, splitValue: userAmount };
    });
    return results;
  }

  if (splitType === 'percentage' && percentages) {
    let usedMinor = 0;
    let usedUsd = 0;
    return splitWith.map((userId, i) => {
      const pct = percentages[userId] ?? 0;
      const isLast = i === splitWith.length - 1;
      const userMinor = isLast ? totalMinor - usedMinor : Math.round(totalMinor * pct / 100);
      const userUsd = isLast ? totalMinorUsd - usedUsd : Math.round(totalMinorUsd * pct / 100);
      usedMinor += userMinor;
      usedUsd += userUsd;
      return { userId, amountMinor: userMinor, amountMinorUsd: userUsd, splitValue: pct };
    });
  }

  if (splitType === 'shares' && shares) {
    const totalShares = Object.values(shares).reduce((s, v) => s + v, 0);
    if (totalShares === 0) throw new Error('Total shares cannot be zero');
    let usedMinor = 0;
    let usedUsd = 0;
    return splitWith.map((userId, i) => {
      const userShares = shares[userId] ?? 1;
      const isLast = i === splitWith.length - 1;
      const userMinor = isLast ? totalMinor - usedMinor : Math.round(totalMinor * userShares / totalShares);
      const userUsd = isLast ? totalMinorUsd - usedUsd : Math.round(totalMinorUsd * userShares / totalShares);
      usedMinor += userMinor;
      usedUsd += userUsd;
      return { userId, amountMinor: userMinor, amountMinorUsd: userUsd, splitValue: userShares };
    });
  }

  throw new Error(`Invalid split configuration: ${splitType}`);
}

export interface ExpenseInsertRows {
  expense: Record<string, unknown>;
  splits: Record<string, unknown>[];
}

/**
 * Compute the expense + split DB rows (currency conversion, splits) WITHOUT
 * inserting. Shared by createExpense (single insert) and the batch import RPC.
 */
export async function buildExpenseRows(data: CreateExpenseData): Promise<ExpenseInsertRows> {
  const minorUnits = getMinorUnitsSync(data.currencyCode);
  const amountMinorSrc = toMinorUnits(data.amount, minorUnits);

  let fxRateToUsd: number;
  let amountMinorUsd: number;

  if (data.currencyCode === 'USD') {
    fxRateToUsd = 1;
    amountMinorUsd = amountMinorSrc;
  } else {
    fxRateToUsd = await fetchExchangeRate(data.currencyCode);
    const usdMinorUnits = 2;
    const decimalAmount = data.amount * fxRateToUsd;
    amountMinorUsd = toMinorUnits(decimalAmount, usdMinorUnits);
  }

  const splits = calculateSplits(
    amountMinorSrc,
    amountMinorUsd,
    data.splitType,
    data.splitWith,
    data.exactAmounts,
    data.percentages,
    data.shares,
    data.currencyCode
  );

  const expenseId = crypto.randomUUID();
  const now = new Date().toISOString();

  const expense = {
    id: expenseId,
    group_id: data.groupId,
    description: data.description,
    amount_minor_src: amountMinorSrc,
    currency_code_src: data.currencyCode,
    amount_minor_usd: amountMinorUsd,
    fx_rate_to_usd: fxRateToUsd,
    paid_by: data.paidBy,
    category_id: data.categoryId ?? null,
    expense_date: data.date ?? now,
    receipt_url: data.receipt_url ?? null,
    notes: data.notes ?? null,
    split_type: data.splitType,
    created_by: data.createdBy,
    created_at: now,
    is_custom_fx_rate: data.customFxRate != null,
  };

  const splitRows = splits.map(s => ({
    id: crypto.randomUUID(),
    expense_id: expenseId,
    group_id: data.groupId,
    user_id: s.userId,
    amount_minor: s.amountMinor,
    amount_minor_usd: s.amountMinorUsd,
    split_value: s.splitValue ?? null,
    participant_display_name: data.participantDisplayNames?.[s.userId] ?? null,
    created_at: now,
  }));

  return { expense, splits: splitRows };
}

export async function createExpense(data: CreateExpenseData): Promise<string> {
  const { expense, splits } = await buildExpenseRows(data);

  const { error: expError } = await supabase.from('expenses').insert(expense);
  if (expError) throw expError;

  const { error: splitError } = await supabase.from('expense_splits').insert(splits);
  if (splitError) throw splitError;

  return expense.id as string;
}

export async function updateExpense(
  expenseId: string,
  groupId: string,
  updates: {
    description?: string;
    amount?: number;
    paidBy?: string;
    categoryId?: string;
    date?: string;
    splitType?: SplitType;
    splitWith?: string[];
    exactAmounts?: Record<string, number>;
    percentages?: Record<string, number>;
    shares?: Record<string, number>;
  }
): Promise<void> {
  // First get current expense to know currency
  const { data: current, error: fetchErr } = await supabase
    .from('expenses')
    .select('currency_code_src, fx_rate_to_usd')
    .eq('id', expenseId)
    .single();

  if (fetchErr || !current) throw fetchErr ?? new Error('Expense not found');

  const currencyCode = current.currency_code_src;
  const minorUnits = getMinorUnitsSync(currencyCode);

  const dbUpdates: Record<string, unknown> = {
    updated_at: new Date().toISOString(),
  };

  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.paidBy !== undefined) dbUpdates.paid_by = updates.paidBy;
  if (updates.categoryId !== undefined) dbUpdates.category_id = updates.categoryId || null;
  if (updates.date !== undefined) dbUpdates.expense_date = updates.date;
  if (updates.splitType !== undefined) dbUpdates.split_type = updates.splitType;

  if (updates.amount !== undefined) {
    const amountMinorSrc = toMinorUnits(updates.amount, minorUnits);
    const fxRate = current.fx_rate_to_usd ?? 1;
    const amountMinorUsd = currencyCode === 'USD'
      ? amountMinorSrc
      : toMinorUnits(updates.amount * fxRate, 2);

    dbUpdates.amount_minor_src = amountMinorSrc;
    dbUpdates.amount_minor_usd = amountMinorUsd;
  }

  const { error: updateErr } = await supabase
    .from('expenses')
    .update(dbUpdates)
    .eq('id', expenseId);

  if (updateErr) throw updateErr;

  // If split config changed, diff-update splits (matches mobile app pattern)
  if (updates.splitWith && updates.splitType) {
    const amountMinorSrc = updates.amount
      ? toMinorUnits(updates.amount, minorUnits)
      : (await supabase.from('expenses').select('amount_minor_src, amount_minor_usd').eq('id', expenseId).single()).data?.amount_minor_src ?? 0;

    const amountMinorUsd = updates.amount
      ? (currencyCode === 'USD' ? toMinorUnits(updates.amount, 2) : toMinorUnits(updates.amount * (current.fx_rate_to_usd ?? 1), 2))
      : (await supabase.from('expenses').select('amount_minor_usd').eq('id', expenseId).single()).data?.amount_minor_usd ?? 0;

    const newSplits = calculateSplits(
      amountMinorSrc,
      amountMinorUsd,
      updates.splitType,
      updates.splitWith,
      updates.exactAmounts,
      updates.percentages,
      updates.shares,
      currencyCode,
    );

    // Fetch all existing splits (including soft-deleted) to respect UNIQUE(expense_id, user_id)
    const { data: existingSplits } = await supabase
      .from('expense_splits')
      .select('id, user_id, _deleted')
      .eq('expense_id', expenseId);

    const existingByUser = new Map(
      (existingSplits ?? []).map(s => [s.user_id, s])
    );
    const newUserIds = new Set(newSplits.map(s => s.userId));
    const now = new Date().toISOString();

    // Update existing rows (including un-deleting soft-deleted ones)
    for (const s of newSplits) {
      const existing = existingByUser.get(s.userId);
      if (existing) {
        await supabase.from('expense_splits')
          .update({
            amount_minor: s.amountMinor,
            amount_minor_usd: s.amountMinorUsd,
            split_value: s.splitValue ?? null,
            _deleted: false,
          })
          .eq('id', existing.id);
      } else {
        await supabase.from('expense_splits').insert({
          id: crypto.randomUUID(),
          expense_id: expenseId,
          group_id: groupId,
          user_id: s.userId,
          amount_minor: s.amountMinor,
          amount_minor_usd: s.amountMinorUsd,
          split_value: s.splitValue ?? null,
          created_at: now,
        });
      }
    }

    // Soft-delete splits for removed users
    const removedIds = (existingSplits ?? [])
      .filter(s => !newUserIds.has(s.user_id) && !s._deleted)
      .map(s => s.id);

    if (removedIds.length > 0) {
      await supabase.from('expense_splits')
        .update({ _deleted: true })
        .in('id', removedIds);
    }
  }
}

export async function deleteExpense(expenseId: string): Promise<void> {
  const { error } = await supabase
    .from('expenses')
    .update({ _deleted: true })
    .eq('id', expenseId);

  if (error) throw error;
}
